import { useCallback } from 'react';

import { useDebounce, useUrlState } from '@/shared/lib/hooks';

import { clampWeek } from '../lib/time-machine.utils';

interface UseTimeMachineOptions {
  /** Weeks the project's own data covers — the series length the API returns. */
  weeksAvailable: number;
}

export interface UseTimeMachine {
  /** The week the slider is on — immediate, so the thumb tracks the drag. */
  currentWeek: number;
  minWeek: number;
  maxWeek: number;
  /** Debounced twin of `currentWeek`: weeks back the data is read at. */
  weeksAgo: number;
  setWeek: (week: number) => void;
  stepBackward: () => void;
  stepForward: () => void;
  reset: () => void;
}

/** Same settle time as the consignment filter beside it — the map has no Apply button. */
const WEEK_SETTLE_MS = 300;

/** Module scope on purpose: `useUrlState` memoises its parsers on this object's identity. */
const DEFAULT_TIME_MACHINE_URL_STATE = { weeksAgo: 0 };

/**
 * Legacy sizes its slider from the project's data range rather than a fixed window,
 * and the v2 API says the same thing implicitly: the report series it returns spans
 * exactly that range, so its length is the number of weeks the slider may travel.
 *
 * The chosen week lives in the URL beside the filters and the layers, so a link
 * carries the whole view — "the map five weeks ago" is as shareable as a filtered map,
 * and the choice now survives a reload, not just folding the panel away.
 */
export function useTimeMachine({ weeksAvailable }: UseTimeMachineOptions): UseTimeMachine {
  const [urlState, setUrlState] = useUrlState(DEFAULT_TIME_MACHINE_URL_STATE);

  const maxWeek = 0;
  const minWeek = Math.min(1 - weeksAvailable, 0);

  /**
   * The URL holds the raw offset and the clamp happens on read, never written back:
   * the range is only known once the first response lands, so clamping into the URL
   * would collapse a link to `weeksAgo=5` down to 0 in the moment before the data
   * arrives — the link would destroy itself while loading. `Number.isFinite` guards
   * the same edge from the other side, since a hand-typed `weeksAgo=abc` parses to NaN.
   */
  const requested = Number.isFinite(urlState.weeksAgo) ? Math.abs(urlState.weeksAgo) : 0;
  // `0 - x`, not `-x`: negating zero yields -0, which then fails every `=== 0` check.
  const currentWeek = clampWeek(0 - requested, minWeek, maxWeek);
  const weeksAgo = useDebounce(Math.abs(currentWeek), WEEK_SETTLE_MS);

  const setWeek = useCallback(
    (next: number) => setUrlState({ weeksAgo: Math.abs(clampWeek(next, minWeek, maxWeek)) }),
    [minWeek, maxWeek, setUrlState]
  );

  return {
    currentWeek,
    minWeek,
    maxWeek,
    weeksAgo,
    setWeek,
    stepBackward: useCallback(() => setWeek(currentWeek - 1), [setWeek, currentWeek]),
    stepForward: useCallback(() => setWeek(currentWeek + 1), [setWeek, currentWeek]),
    reset: useCallback(() => setUrlState({ weeksAgo: 0 }), [setUrlState])
  };
}
